import React, { useCallback, useMemo, useRef, useState } from "react";

import { Box, Button, Chip, Popover, Stack, Typography } from "@mui/material";
import { QuickViewMeasureFilter, QuickViewTableColumn } from "store/types";

import { getMeasureFilterMode, getMeasureFilterValue, MeasureFilterMode, MeasureInput } from "./MeasureInput";

interface IMeasureFiltersProps {
  columns: QuickViewTableColumn[];
  measureFilters: QuickViewMeasureFilter;
  onMeasureFilterChange: (measureFilters: QuickViewMeasureFilter) => void;
  container?: Element | null;
}

const getModeLabel = (filter: { from_value?: number; to_value?: number }): string => {
  if (filter.from_value !== undefined && filter.to_value !== undefined) return `= ${filter.from_value}`;
  if (filter.from_value !== undefined) return `> ${filter.from_value}`;
  if (filter.to_value !== undefined) return `< ${filter.to_value}`;
  return "";
};

export const MeasureFilters: React.FC<IMeasureFiltersProps> = ({ columns, measureFilters, onMeasureFilterChange, container }) => {
  const [editKey, setEditKey] = useState<string | null>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const draftRef = useRef<{ value: number | ""; mode: MeasureFilterMode }>({
    value: "",
    mode: MeasureFilterMode.GreaterThan
  });

  const columnMap = useMemo(
    () =>
      columns.reduce<Record<string, QuickViewTableColumn>>((acc, col) => {
        if (col.type === "measure") acc[col.key] = col;
        return acc;
      }, {}),
    [columns]
  );

  const handleDelete = useCallback(
    (key: string) => {
      const updated = { ...measureFilters };
      delete updated[key];
      onMeasureFilterChange(updated);
    },
    [measureFilters, onMeasureFilterChange]
  );

  const handleChipClick = useCallback(
    (e: React.MouseEvent<HTMLElement>, key: string) => {
      anchorRef.current = e.currentTarget;
      draftRef.current = {
        value: getMeasureFilterValue(measureFilters[key]),
        mode: getMeasureFilterMode(measureFilters[key])
      };
      setEditKey(key);
    },
    [measureFilters]
  );

  const handleClose = useCallback(() => {
    setEditKey(null);
    anchorRef.current = null;
  }, []);

  const handleDraftChange = useCallback((_columnKey: string, value: number | "", mode: MeasureFilterMode) => {
    draftRef.current = { value, mode };
  }, []);

  const handleSave = useCallback(() => {
    if (!editKey) return;
    const { value, mode } = draftRef.current;
    const updated = { ...measureFilters };

    if (value === "") {
      delete updated[editKey];
      onMeasureFilterChange(updated);
      handleClose();
      return;
    }

    switch (mode) {
      case MeasureFilterMode.GreaterThan:
        updated[editKey] = { from_value: value };
        break;
      case MeasureFilterMode.LessThan:
        updated[editKey] = { to_value: value };
        break;
      case MeasureFilterMode.Equal:
        updated[editKey] = { from_value: value, to_value: value };
        break;
    }
    onMeasureFilterChange(updated);
    handleClose();
  }, [editKey, measureFilters, onMeasureFilterChange, handleClose]);

  const activeKeys = Object.keys(measureFilters);
  if (!activeKeys.length) return null;

  return (
    <>
      {activeKeys.map((key) => {
        const column = columnMap[key];
        const filter = measureFilters[key];
        if (!column || !filter) return null;

        return (
          <Chip
            key={key}
            size="small"
            label={`${column.title}: ${getModeLabel(filter)}`}
            onClick={(e) => handleChipClick(e, key)}
            onDelete={() => handleDelete(key)}
            sx={{ height: "26px", fontSize: "15px", color: "#0a3142" }}
          />
        );
      })}

      <Popover
        open={Boolean(editKey)}
        anchorEl={anchorRef.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        container={container ?? document.body}
        disableEnforceFocus
        disableAutoFocus
      >
        {editKey && columnMap[editKey] && (
          <Stack sx={{ alignItems: "stretch", p: "30px", gap: "30px", maxWidth: "500px" }}>
            <Stack
              sx={{ borderBottom: "1px solid #dae0e2", pb: "5px" }}
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography sx={{ fontSize: "15px" }}>{columnMap[editKey].title}</Typography>
              <Stack flexDirection="row">
                <Button sx={{ color: "#26A6AE" }} variant="text" onClick={handleClose}>
                  Cancel
                </Button>
                <Button
                  sx={{ color: "#26A6AE", ":disabled": { backgroundColor: "#FFF", color: "#bac7ce" } }}
                  variant="text"
                  onClick={handleSave}
                >
                  Save
                </Button>
              </Stack>
            </Stack>
            <Box sx={{ maxWidth: "300px", minWidth: "300px" }}>
              <MeasureInput
                columnTitle={columnMap[editKey].title}
                columnKey={editKey}
                value={getMeasureFilterValue(measureFilters[editKey])}
                mode={getMeasureFilterMode(measureFilters[editKey])}
                onChange={handleDraftChange}
                container={container}
              />
            </Box>
          </Stack>
        )}
      </Popover>
    </>
  );
};
